
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Week } from "../types/workout-types";
import { processData } from "../utils/data-processor";
import { toast } from "sonner";

interface UseRealtimeWorkoutDataProps {
  programId: string | undefined;
}

interface UseRealtimeWorkoutDataReturn {
  weeks: Week[];
  setWeeks: React.Dispatch<React.SetStateAction<Week[]>>;
  loading: boolean;
  dataReceived: boolean;
  waitingForData: boolean;
}

export function useRealtimeWorkoutData({
  programId
}: UseRealtimeWorkoutDataProps): UseRealtimeWorkoutDataReturn {
  const [weeks, setWeeks] = useState<Week[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataReceived, setDataReceived] = useState(false);
  const [waitingForData, setWaitingForData] = useState(false);

  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramData = async () => {
      try {
        setLoading(true);
        
        const { data: programDataResult, error: programDataError } = await supabase
          .from('program_data')
          .select('data')
          .eq('program_id', programId)
          .maybeSingle();
        
        if (programDataError) {
          console.error("Error fetching program data:", programDataError);
          return;
        }
        
        if (programDataResult?.data && Object.keys(programDataResult.data).length > 0) {
          console.log("Initial program data loaded:", programDataResult.data);
          const processedWeeks = processData(programDataResult.data);
          if (processedWeeks.length > 0) {
            setWeeks(processedWeeks);
          }
          setDataReceived(true);
          setWaitingForData(false);
        } else {
          console.log("No program data available yet, waiting for real-time updates...");
          setWaitingForData(true);
        }
      } catch (error) {
        console.error("Error processing program data:", error);
        toast.error("Failed to load program data");
      } finally {
        setLoading(false);
      }
    };
    
    // Set up real-time subscription channel
    const channel = supabase
      .channel('program_data_changes')
      .on(
        'postgres_changes', 
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'program_data',
          filter: `program_id=eq.${programId}`
        },
        (payload) => {
          console.log('Real-time update received:', payload);
          if (payload.new && payload.new.data) {
            const newData = payload.new.data;
            
            // Only process if data is not empty
            if (Object.keys(newData).length > 0) {
              const processedWeeks = processData(newData);
              if (processedWeeks.length > 0) {
                setWeeks(processedWeeks);
              }
              setDataReceived(true);
              setWaitingForData(false);
              
              // Only show toast if this isn't the first data we're receiving
              if (dataReceived) {
                toast.success("Program data updated");
              } else {
                toast.success("Program data loaded");
              }
            }
          }
        }
      )
      .subscribe((status) => {
        console.log(`Realtime subscription status: ${status}`);
      });

    fetchProgramData();
    
    return () => {
      console.log("Cleaning up real-time subscription");
      supabase.removeChannel(channel);
    };
  }, [programId, dataReceived]);

  return {
    weeks,
    setWeeks,
    loading,
    dataReceived,
    waitingForData
  };
}
